import React, { useState } from 'react';
import {
    Box,
    Typography,
    Grid,
    Paper,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Chip,
    Card,
    CardContent,
    Link,
    Button
} from '@mui/material';

import {
    Checklist,
    LocalShipping,
    AttachMoney,
    SupportAgent,
    EmojiPeople,
    CheckCircle,
    TrendingUp,
    Home,
    Star,
    ArrowForwardIos,
    Info
} from '@mui/icons-material';

// スクロール用の関数
const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
        const offset = 80; // ヘッダーの高さ分のオフセット
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;

        window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
        });
    }
};

const tocItems = [
    { id: 'introduction', label: 'ルームシェア引っ越しのコツと重要性' },
    { id: 'select', label: '引っ越し業者の選び方' },
    { id: 'price', label: '主要業者の料金比較' },
    { id: 'service', label: 'サービス・オプションの違い' },
    { id: 'experience', label: '実際の体験談＆失敗しないためのポイント' },
    { id: 'advice', label: '専門家からのアドバイス' },
    { id: 'summary', label: 'まとめ' }
];

const PostContent = () => {
    const [showToc, setShowToc] = useState(true);
    return (
        <Box sx={{ maxWidth: 800, mx: 'auto', px: 3, pb: 4 }}>
            {showToc ? (
                <Card sx={{ mb: 4, boxShadow: 2, bgcolor: '#e3f2fd', border: '1px solid #ddd', borderRadius: 2 }}>
                    <CardContent sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Typography variant="h6" sx={{ flexGrow: 1 }}>目次</Typography>
                            <Button variant="outlined" size="small" onClick={() => setShowToc(false)}>閉じる</Button>
                        </Box>
                        <List sx={{ p: 0, pt: 0.5 }}>
                            {tocItems.map((item, idx) => (
                                <ListItem key={item.id} sx={{ p: 0.5, display: 'flex', alignItems: 'center', minHeight: 36 }}>
                                    <Box sx={{ minWidth: 32, minHeight: 32, bgcolor: '#f5f5f5', color: '#888', fontWeight: 'bold', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1, fontSize: '1.1rem' }}>
                                        {idx + 1}.
                                    </Box>
                                    <Link component="button" onClick={() => scrollToSection(item.id)} color="inherit" underline="hover" sx={{ cursor: 'pointer', fontSize: '0.98rem', textAlign: 'left', pl: 0 }}>
                                        {item.label}
                                    </Link>
                                </ListItem>
                            ))}
                        </List>
                    </CardContent>
                </Card>
            ) : (
                <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setShowToc(true)}
                    sx={{ mb: 2 }}
                >
                    目次を表示
                </Button>
            )}

            {/* 導入 */}
            <Box id="introduction" sx={{ mb: 5 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    ルームシェア引っ越しのコツと重要性
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ルームシェアの新生活は、ワクワクと共に少しの不安もつきもの。特に「引っ越し」は、荷物の量やスケジュール、費用、同居人との調整など、ソロ引っ越し以上に気を使うポイントがたくさんあります。<b>どの引っ越し業者を選ぶか</b>は、その後の生活の快適さやスタートダッシュに大きく影響します。
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    この記事では、ルームシェア経験者や多くの引っ越しサポートをしてきた立場から、<b>おすすめの引っ越し業者の比較</b>、料金やサービス内容、実際の体験談まで、実用的な情報を徹底解説します。
                </Typography>
                <Box
                    component="img"
                    src="/images/blog/roommates-moving-boxes_roomshare-move-best-company-comparison.jpg"
                    alt="明るい共有リビングで、男女4人のルームメイトが段ボールを運び入れたり笑顔で協力しながら新生活の引っ越し作業をしている様子。リビングには観葉植物やカラフルなクッションがあり、和やかで活気ある雰囲気が感じられる。"
                    sx={{ width: '100%', maxHeight: 310, objectFit: 'cover', borderRadius: 2, my: 2, boxShadow: 1 }}
                />
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    ※この記事は2024年6月時点の情報に基づいています。最新情報は各業者公式サイト等でご確認ください。
                </Typography>
            </Box>

            {/* 業者の選び方 */}
            <Box id="select" sx={{ mb: 5 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    <Checklist sx={{ verticalAlign: 'middle', mr: 1, color: 'primary.main' }} />
                    引っ越し業者の選び方
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ルームシェアの引っ越しでは、<b>「安さ」だけでなく「柔軟性」や「サービスの質」</b>も重要な選択基準です。以下のポイントを押さえて業者を比較しましょう。
                </Typography>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={6}>
                        <Paper sx={{ p: 2, borderRadius: 2, height: '100%' }} elevation={2}>
                            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <AttachMoney sx={{ mr: 1, color: 'success.main' }} /> 料金の明瞭さ
                            </Typography>
                            <Typography variant="body2">
                                見積もりが明確かつ追加料金のリスクが低い業者を選ぶと安心。<b>荷物量が少ない場合や共有家具のみの配送</b>など、ルームシェア特有の事情にも対応しているか要チェック。
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Paper sx={{ p: 2, borderRadius: 2, height: '100%' }} elevation={2}>
                            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <SupportAgent sx={{ mr: 1, color: 'info.main' }} /> 柔軟なサービス
                            </Typography>
                            <Typography variant="body2">
                                日時指定や分割配送、同居人とのまとめて引っ越しなど、<b>カスタマイズ性の高さ</b>が重要です。オンライン見積もりやLINE相談可能な業者も増えています。
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12}>
                        <Paper sx={{ p: 2, borderRadius: 2, mt: 1 }} elevation={2}>
                            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <LocalShipping sx={{ mr: 1, color: 'primary.main' }} /> 対応エリアと対応力
                            </Typography>
                            <Typography variant="body2">
                                主要都市間か、地方にも強いか、エリアによって選択肢が変わります。<b>ルームシェア物件特有の搬入制限</b>（狭い階段やエレベーター利用など）への慣れも大切！
                            </Typography>
                        </Paper>
                    </Grid>
                </Grid>
                <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                        <CheckCircle sx={{ fontSize: 18, color: 'success.main', mr: 0.5, verticalAlign: 'middle' }} />
                        <b>ポイント</b>：複数社の一括見積もりサイトを活用し、柔軟に比較しましょう。
                    </Typography>
                </Box>
            </Box>

            {/* 料金比較 */}
            <Box id="price" sx={{ mb: 5 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    <AttachMoney sx={{ verticalAlign: 'middle', mr: 1, color: 'success.main' }} />
                    主要業者の料金比較
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ルームシェアでよく使われる大手＆コスパ系引っ越し業者の料金目安を比較表でご紹介します（2024年6月時点）。<b>荷物量・距離・時期</b>によって大きく変動しますので、目安としてご覧ください。
                </Typography>

                <Box sx={{ overflowX: 'auto', mb: 2 }}>
                    <table style={{ borderCollapse: 'collapse', width: '100%', background: '#fff', borderRadius: '12px', minWidth: '520px' }}>
                        <thead>
                            <tr style={{ background: '#e3f2fd' }}>
                                <th style={{ padding: '10px 6px', border: '1px solid #ddd', fontWeight: 700 }}>業者名</th>
                                <th style={{ padding: '10px 6px', border: '1px solid #ddd', fontWeight: 700 }}>単身/小荷物</th>
                                <th style={{ padding: '10px 6px', border: '1px solid #ddd', fontWeight: 700 }}>2人分目安</th>
                                <th style={{ padding: '10px 6px', border: '1px solid #ddd', fontWeight: 700 }}>特徴</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td style={{ padding: '10px 6px', border: '1px solid #ddd' }}><b>サカイ引越センター</b></td>
                                <td style={{ padding: '10px 6px', border: '1px solid #ddd' }}>約34,000円～</td>
                                <td style={{ padding: '10px 6px', border: '1px solid #ddd' }}>約60,000円～</td>
                                <td style={{ padding: '10px 6px', border: '1px solid #ddd' }}>全国対応・安心感とコスパで人気</td>
                            </tr>
                            <tr>
                                <td style={{ padding: '10px 6px', border: '1px solid #ddd' }}><b>アート引越センター</b></td>
                                <td style={{ padding: '10px 6px', border: '1px solid #ddd' }}>約35,000円～</td>
                                <td style={{ padding: '10px 6px', border: '1px solid #ddd' }}>約65,000円～</td>
                                <td style={{ padding: '10px 6px', border: '1px solid #ddd' }}>丁寧な対応・女性スタッフ指定可</td>
                            </tr>
                            <tr>
                                <td style={{ padding: '10px 6px', border: '1px solid #ddd' }}><b>ハート引越センター</b></td>
                                <td style={{ padding: '10px 6px', border: '1px solid #ddd' }}>約30,000円～</td>
                                <td style={{ padding: '10px 6px', border: '1px solid #ddd' }}>約50,000円～</td>
                                <td style={{ padding: '10px 6px', border: '1px solid #ddd' }}>価格重視・柔軟なパックで対応</td>
                            </tr>
                            <tr>
                                <td style={{ padding: '10px 6px', border: '1px solid #ddd' }}><b>赤帽</b></td>
                                <td style={{ padding: '10px 6px', border: '1px solid #ddd' }}>約13,500円～</td>
                                <td style={{ padding: '10px 6px', border: '1px solid #ddd' }}>約25,000円～</td>
                                <td style={{ padding: '10px 6px', border: '1px solid #ddd' }}>短距離や荷物少なめに最適</td>
                            </tr>
                            <tr>
                                <td style={{ padding: '10px 6px', border: '1px solid #ddd' }}><b>くらしのマーケット</b></td>
                                <td style={{ padding: '10px 6px', border: '1px solid #ddd' }}>約10,000円～</td>
                                <td style={{ padding: '10px 6px', border: '1px solid #ddd' }}>約23,000円～</td>
                                <td style={{ padding: '10px 6px', border: '1px solid #ddd' }}>地元業者・格安＆口コミ重視</td>
                            </tr>
                        </tbody>
                    </table>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    ※金額は東京23区～23区内・平日・繁忙期外の一例。実際の荷物量・距離・オプションにより大きく変動します。
                </Typography>

                <Box sx={{ mt: 2 }}>
                    <Chip label="格安業者" color="primary" variant="outlined" sx={{ mr: 1 }} />
                    <Chip label="大手の安心" color="success" variant="outlined" />
                </Box>
            </Box>

            {/* サービス比較 */}
            <Box id="service" sx={{ mb: 5 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    <SupportAgent sx={{ verticalAlign: 'middle', mr: 1, color: 'info.main' }} />
                    サービス・オプションの違い
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mb: 2 }}>
                    料金だけでなく、<b>サービス内容やオプションの充実度</b>も要注目。ルームシェアならではの利便性を高めるサービスを比較します。
                </Typography>
                <Grid container spacing={2} sx={{ mb: 1 }}>
                    <Grid item xs={12} sm={6}>
                        <Card sx={{ maxWidth: 345, mb: 2, borderRadius: 2, boxShadow: 1 }}>
                            <CardContent>
                                <Typography variant="h6" sx={{ mb: 1 }}><Star sx={{ color: 'warning.main', mr: 1 }} /> 大手業者の特長</Typography>
                                <List dense>
                                    <ListItem>
                                        <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                                        <ListItemText primary="梱包資材（ダンボール・ガムテープ）無料" />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                                        <ListItemText primary="家具の分解・組立サポート" />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                                        <ListItemText primary="不用品回収・買い取りの提案" />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                                        <ListItemText primary="女性スタッフ対応・立ち合い代行" />
                                    </ListItem>
                                </List>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Card sx={{ maxWidth: 345, mb: 2, borderRadius: 2, boxShadow: 1 }}>
                            <CardContent>
                                <Typography variant="h6" sx={{ mb: 1 }}><TrendingUp sx={{ color: 'primary.main', mr: 1 }} /> 地元＆格安系の特長</Typography>
                                <List dense>
                                    <ListItem>
                                        <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                                        <ListItemText primary="単身・小荷物向けの超格安パック" />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                                        <ListItemText primary="スケジュールの柔軟対応（夜間・早朝も可）" />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                                        <ListItemText primary="LINEやアプリで即見積り" />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                                        <ListItemText primary="担当制で融通が効きやすい" />
                                    </ListItem>
                                </List>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                <Typography variant="body2" color="text.secondary">
                    <Info sx={{ fontSize: 18, color: 'info.main', mr: 0.5, verticalAlign: 'middle' }} />
                    <b>TIP:</b> 梱包や不用品回収を自分たちで手配すると、さらにコストカットも可能です。
                </Typography>
            </Box>

            {/* 体験談 */}
            <Box id="experience" sx={{ mb: 5 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    <EmojiPeople sx={{ verticalAlign: 'middle', mr: 1, color: 'secondary.main' }} />
                    実際の体験談＆失敗しないためのポイント
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ここでは、実際にルームシェアの引っ越しを経験した方のリアルな声と、スムーズな引っ越しのためのコツをご紹介します。
                </Typography>
                <Grid container spacing={3} sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={6}>
                        <Paper sx={{ p: 2, borderRadius: 2, mb: 2, boxShadow: 1 }} elevation={1}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Case 1：同居人3人まとめて引っ越し</Typography>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                <b>「荷物の量がバラバラだったので、サカイ引越センターにまとめて見積もり依頼。担当者が現場で仕分けルールを提案してくれ、効率的に搬入できました。共同の家具は別便で運んでもらい、トラブルもなし！」（30代・女性）</b>
                            </Typography>
                            <Divider sx={{ my: 1 }} />
                            <Typography variant="body2" color="text.secondary">
                                ポイント：<b>「誰が何を運ぶのか」を事前に明確に！</b>
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Paper sx={{ p: 2, borderRadius: 2, mb: 2, boxShadow: 1 }} elevation={1}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Case 2：赤帽で格安＆柔軟引っ越し</Typography>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                <b>「荷物が少なかったので赤帽に依頼。引っ越し先のエレベーターが狭かったけど、ドライバーさんがテキパキ対応してくれて助かりました。LINEで気軽に相談できたのも◎」（20代・男性）</b>
                            </Typography>
                            <Divider sx={{ my: 1 }} />
                            <Typography variant="body2" color="text.secondary">
                                ポイント：<b>地元業者は柔軟な対応が強み</b>
                            </Typography>
                        </Paper>
                    </Grid>
                </Grid>

                <List dense sx={{ mt: 2 }}>
                    <ListItem>
                        <ListItemIcon><ArrowForwardIos fontSize="small" color="primary" /></ListItemIcon>
                        <ListItemText primary="搬入経路（階段・エレベーター・道路幅）を事前に確認" />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon><ArrowForwardIos fontSize="small" color="primary" /></ListItemIcon>
                        <ListItemText primary="共有家具や家電の所有者・搬入順をメンバー間で明確に" />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon><ArrowForwardIos fontSize="small" color="primary" /></ListItemIcon>
                        <ListItemText primary="不用品は早めに処分 or 買い取りサービスを併用" />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon><ArrowForwardIos fontSize="small" color="primary" /></ListItemIcon>
                        <ListItemText primary="人数分の荷物管理リストを作成し、当日混乱を防止" />
                    </ListItem>
                </List>
            </Box>

            {/* 専門家からのアドバイス */}
            <Box id="advice" sx={{ mb: 5 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    <Home sx={{ verticalAlign: 'middle', mr: 1, color: 'primary.main' }} />
                    専門家からのアドバイス
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ルームシェアの引っ越しは、普通の引っ越しとは一味違った注意点があります。<b>複数人でのスケジューリングや、共有物の管理、個人情報の扱い</b>など、事前準備が肝心です。
                </Typography>
                <Paper sx={{ p: 2, borderRadius: 2, bgcolor: '#f1f8e9', mb: 2, boxShadow: 1 }}>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold', color: 'success.dark' }}>
                        【アドバイス1】<TrendingUp sx={{ verticalAlign: 'middle', color: 'success.main', mr: 1 }} />繁忙期（3〜4月）は早めの予約必須
                    </Typography>
                    <Typography variant="body2">
                        春の引っ越しシーズンは、人気業者の予約がすぐ埋まります。<b>1〜2ヶ月前の見積もり・予約</b>が基本です。オフシーズンや平日を狙えば、費用もグッと抑えられます。
                    </Typography>
                </Paper>
                <Paper sx={{ p: 2, borderRadius: 2, bgcolor: '#e3f2fd', mb: 2, boxShadow: 1 }}>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold', color: 'primary.dark' }}>
                        【アドバイス2】<Checklist sx={{ verticalAlign: 'middle', color: 'primary.main', mr: 1 }} />一括見積もりサイトで効率化
                    </Typography>
                    <Typography variant="body2">
                        主要な一括見積サイト（<b>SUUMO引越し見積もり・引越し侍</b>など）を使えば、複数社の価格やサービスを比較しやすく、相場観もつかみやすいです。ただしめちゃくちゃメールが来るのでサブメアド等での登録がおすすめです。
                    </Typography>
                </Paper>
                <Paper sx={{ p: 2, borderRadius: 2, bgcolor: '#fffde7', mb: 2, boxShadow: 1 }}>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold', color: 'warning.dark' }}>
                        【アドバイス3】<SupportAgent sx={{ verticalAlign: 'middle', color: 'warning.main', mr: 1 }} />当日トラブル防止には「連絡手段」と「チェックリスト」
                    </Typography>
                    <Typography variant="body2">
                        グループLINE等で進捗共有を。荷物リストやタイムテーブルを作成し、当日の混乱を避けましょう。
                    </Typography>
                </Paper>
            </Box>

            {/* まとめ */}
            <Box id="summary">
                <Typography variant="h4" component="h2" gutterBottom>
                    <Star sx={{ verticalAlign: 'middle', mr: 1, color: 'warning.main' }} />
                    まとめ
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ルームシェアの引っ越しは、<b>料金・サービス・柔軟性・体験談</b>を総合的に比較することが成功のカギです。大手業者の安心感、格安業者の柔軟性、オプションサービスの活用など、あなたやルームメイトに合ったプランを見つけて、快適な新生活をスタートさせましょう！
                </Typography>
                <Box sx={{ mt: 3 }}>
                    <Button
                        variant="outlined"
                        color="primary"
                        size="large"
                        href="https://www.hikkoshi-samurai.jp/"
                        target="_blank"
                        sx={{ mr: 2 }}
                    >
                        一括見積もりサイトで比較
                    </Button>
                    <Button
                        variant="outlined"
                        color="success"
                        size="large"
                        href="https://www.sakai-move.co.jp/"
                        target="_blank"
                    >
                        サカイ引越センター公式サイト
                    </Button>
                </Box>
            </Box>
        </Box>
    );
};

const post = {
    id: 'roomshare-move-best-company-comparison',
    slug: 'roomshare-move-best-company-comparison',
    title: 'ルームシェアの「おすすめ引っ越し業者」比較',
    excerpt: '引っ越し業者の選び方、料金比較、サービスの違い、実際の体験談まで、ルームシェアに最適な引っ越し業者を徹底比較します。',
    coverImage: '/images/blog/cover-roomshare-move_roomshare-move-best-company-comparison.jpg',
    coverImageAlt: '明るい自然光が差し込むモダンなルームシェアの共有リビングで、多様な背景を持つルームメイトたちが楽しく会話をしている様子。空間には観葉植物やカラフルな雑貨が置かれ、リラックスした雰囲気と新生活への期待感が感じられる。',
    publishedAt: '2024-06-12T00:00:00.000Z',
    isPublished: true,
    categories: ['ルームシェア', '引っ越し', '新生活'],
    tags: ['引っ越し業者', 'ルームシェア', '料金比較', '体験談'],
    author: {
        name: '井上 真理',
        avatar: '/images/avatars/inoue-mari.jpg',
        bio: 'シェアハウス運営・引っ越しサポートに10年以上携わる、住まいと暮らしのアドバイザー。実体験と最新動向をもとに、実用的な情報を発信中。',
        profilePicture: '/images/avatars/inoue-mari.jpg'
    },
    viewCount: 1240,
    contentComponent: PostContent
};

export default post;